import React, { useContext, useEffect } from 'react'; //eslint-disable-line
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { useHistory } from 'react-router-dom'; //eslint-disable-line
import Select from 'react-select';
import FormHelperText from '@material-ui/core/FormHelperText';

import locationStoreContext from '../stores/locationStore';
import viewStoreContext from '../stores/viewStore';
import * as CONSTS from '../utils/constants';
import '../i18n';

const Selection = observer(() => {
  const locationStore = useContext(locationStoreContext);
  const viewStore = useContext(viewStoreContext);
  const history = useHistory();

  const { t } = useTranslation();

  useEffect(() => {
    locationStore.loadTripsByCorridorAndStation();
  }, [locationStore.corridors]);

  useEffect(() => {
    viewStore.updateView(CONSTS.VIEW_PATHS.HOME);
  }, []);

  const handleLineChange = e => {
    const code = e ? e.code : '';
    const label = e ? e.label : '';
    locationStore.setCorridor(code, label);
  };

  /** */
  const handleStationChange = e => {
    const value = e ? e.value : '';

    locationStore.setStation(value);

    // Skip setting the station's corridor if the station is not found.
    if (locationStore.station === undefined) return;

    // If corridor is null or corridor code is -1, it means a specific corridor have not been selected and need to be auto populated.
    if (!locationStore.corridor || locationStore.corridor.code === -1) {
      locationStore.setCorridor(locationStore.station.corridorCode);
    }

    viewStore.resetTripRowIncrement(CONSTS.SERVICE_DIRECTION_ALL);
  };

  const handleStationDeparture = () => {
    if (!locationStore.isSelectionValid || !locationStore.corridor || !locationStore.station) return;

    locationStore.resetDepartureData();
    history.push(`${viewStore.views.stationDeparture}/${locationStore.corridor.code}/${locationStore.station.code}`, { isLoaded: true});
  };

  const handleBusDeparture = () => {
    if (!locationStore.isSelectionValid || !locationStore.corridor) return;

    locationStore.resetDepartureData();
    const timingPoint = locationStore.station.timingPoint ? locationStore.station.timingPoint : locationStore.station.code;
    history.push(`${viewStore.views.busDeparture}/${CONSTS.BUS_TERMINAL_CODE}/${timingPoint}`, { isLoaded: true});
  };

  const handleUnionBusDeparture = () => {
    if (!locationStore.isSelectionValid || !locationStore.corridor) return;
    locationStore.resetDepartureData();
    if(locationStore.stationCode) {
      // do save
    }
    const timingPoint = locationStore.station.timingPoint ? locationStore.station.timingPoint : locationStore.station.code;
    history.push(`${viewStore.views.usbtDeparture}/${CONSTS.BUS_TERMINAL_CODE}/${timingPoint}`, { isLoaded: true});
  };

  const handleUnionDeparture = () => {
    if (!locationStore.isSelectionValid || !locationStore.corridor || !locationStore.station) return;

    locationStore.resetDepartureData();
    history.push(`${viewStore.views.unionDeparture}/${locationStore.corridor.code}/${locationStore.station.code}`, { isLoaded: true});
  };

  const busStationsToHide = viewStore.busTerminalsToHide();
  const allStopsSorted = () => {
    if (!locationStore.corridor || locationStore.corridor.code === -1) {
      return locationStore.allStops
        .filter(stop => stop.code !== CONSTS.STATION_CODE_UNION)
        .map(stop => {
          return { value: stop.code, label: stop.name };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    if(locationStore.corridor && locationStore.corridorCode === CONSTS.BUS_TERMINAL_CODE) {
      return locationStore.busStations
        .filter(x=> !busStationsToHide.includes(x.code))
        .map(bus => {
          return {value: bus.code, label: bus.description};
        });
    }

    return locationStore.corridor.stops
      .filter(stop => stop.code !== CONSTS.STATION_CODE_UNION)
      .map(stop => {
        return { value: stop.code, label: stop.name };
      });
  };

  const allCorridors = () => {
    if (!locationStore.corridorStations || locationStore.corridorStations.length < 1) return [];

    const corridors = locationStore.corridorStations
      .map(corridor => {
        return { value: corridor.corridorId, label: corridor.name, code: corridor.code };
      });

    corridors.push({ value: -1, label: 'All Lines', code: -1 });
    if (viewStore.canShowBusTerminals()) {
      corridors.push({ value: 0, label: 'Bus Terminals', code: CONSTS.BUS_TERMINAL_CODE });
    }

    return corridors.sort((a, b) => a.value - b.value);
  };

  const loadingIndicator = () => {
    return <i className="fa fa-spinner fa-spin" />;
  };
  // Function for detect small screen by height and scroll down to first select
  const checkInputSearchable = () => {
    let res = true;
    if (window.innerHeight <= 1024) {// 1024 px it is minimum size for screen because keyboard size more 500 px for small device
      res = false;
    }
    return res;
  };

  return (
    <main id="main-content">
      <div className="selection">
        <div className="row">
          <h1>{t('home.heading')}</h1>
          <div className="selection-break"></div>
        </div>
        <div className="row">
          <Select
            options={allCorridors()}
            className="selector"
            classNamePrefix="selector"
            components={{ LoadingIndicator: loadingIndicator }}
            onChange={handleLineChange}
            fullWidth
            value={locationStore.corridor ? { label: locationStore.corridor.name } : []}
            isClearable={true}
            backspaceRemovesValue={true}
            openMenuOnFocus={true}
            placeholder={t('home.lineSelectionLabel')}
            maxMenuHeight={300}
            aria-label={t('home.lineSelectionLabel')}
            aria-selected={locationStore.corridor ? { label: locationStore.corridor.name } : []}
            isLoading={locationStore.isLoading}
            isSearchable={checkInputSearchable()}
          />
        </div>
        <div className="row">
          <Select
            options={allStopsSorted()}
            className="selector"
            classNamePrefix="selector"
            components={{ LoadingIndicator: loadingIndicator }}
            onChange={handleStationChange}
            isClearable={true}
            backspaceRemovesValue={true}
            openMenuOnFocus={true}
            value={locationStore.station && !busStationsToHide.includes(locationStore.station.code) ? { label: locationStore.station.name } : []}
            fullWidth
            placeholder={t('home.stationSelectionLabel')}
            aria-label={t('home.stationSelectionLabel')}
            aria-selected={locationStore.station ? { label: locationStore.station.name } : []}
            aria-required={!locationStore.isSelectionValid}
            maxMenuHeight={200}
            isLoading={locationStore.isLoading}
            isSearchable={checkInputSearchable()}
          />
          {!locationStore.isSelectionValid && (<FormHelperText error={true}>{'Required'}</FormHelperText>)}
        </div>
      </div>
      <div className="selection">
        <div className="row">
          <div className="selection-break"></div>
        </div>
        <div className="row link">
          { locationStore.corridorCode !== CONSTS.BUS_TERMINAL_CODE &&
            <button className="departure" onClick={handleStationDeparture}>
              <h2>
                { t('home.trainDepartureLabel')}
              </h2>
            </button>
          }
          {viewStore.canShowBusTerminals() && (locationStore.station?.hasBus || locationStore.corridorCode === CONSTS.BUS_TERMINAL_CODE)  &&
          <button className="departure" onClick={handleBusDeparture}>
            <h2>
              { t('home.busDepartureLabel')}
            </h2>
          </button>
          }
          {(locationStore.corridorCode !== CONSTS.BUS_TERMINAL_CODE)  &&
          <button className="departure" onClick={handleUnionDeparture}>
            <h2>
              {t('home.unionDepartureLabel')}
            </h2>
          </button>
          }
          {viewStore.canShowBusTerminals() && (locationStore.corridorCode === CONSTS.BUS_TERMINAL_CODE)  &&
          <button className="departure" onClick={handleUnionBusDeparture}>
            <h2>
              { t('home.unionBusDepartureLabel')}
            </h2>
          </button>
          }
        </div>
      </div>
    </main>
  );
});

export default Selection;
